import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, LoginRequest } from '@/types/auth';
import { apiService } from '@/services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<any>;
  applyLoginResponse: (response: any) => any;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const cachedUser = localStorage.getItem('user_data');

    // Utiliser les données en cache pour un affichage rapide
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('user_data');
      }
    }

    // Toujours vérifier avec le serveur (le cookie HttpOnly est envoyé automatiquement)
    // silent: true pour ne pas afficher d'erreur console au login (401 attendu)
    try {
      const userData = await apiService.request<{ success: boolean; data: User }>('/auth/me', { silent: true });
      if (userData && userData.success && userData.data) {
        setUser(userData.data);
        localStorage.setItem('user_data', JSON.stringify(userData.data));
      } else {
        localStorage.removeItem('user_data');
        setUser(null);
      }
    } catch (error) {
      // Seul un 401 signifie que la session n'est plus valable. Traiter toute
      // erreur comme une deconnexion vidait la session sur un quota atteint
      // (429), une panne serveur ou une simple coupure reseau : l'utilisateur
      // etait renvoye au login et perdait son travail en cours. Dans ces cas
      // on garde la session en cache et on retentera au prochain chargement.
      const statut = (error as { status?: number } | null)?.status;
      if (statut === 401) {
        localStorage.removeItem('user_data');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Le geste commun a toute porte d'entree une fois la reponse de connexion
   * en main — mot de passe classique, PIN tactile, empreinte. Le serveur a
   * deja pose le cookie de session ; ici on normalise l'utilisateur et on
   * le garde en cache pour un affichage immediat au prochain chargement.
   */
  const appliquerConnexion = (response: any) => {
    if (!(response && response.success && response.data)) {
      throw new Error('Invalid response format');
    }
    if (response.data.requiresTwoFactor) {
      return response;
    }

    const { user } = response.data as { user: User };

    const normalizedUser = {
      ...user,
      roles: user.roles || [],
      permissions: user.permissions || [],
      farm_id: user.farm_id || null,
      farm: user.farm || null,
      shop_id: user.shop_id || null,
      shop: user.shop || null,
      cold_storage_id: user.cold_storage_id || null,
      cold_storage: user.cold_storage || null
    };

    if (normalizedUser.farm_id) {
      localStorage.setItem('ferme_id', String(normalizedUser.farm_id));
    }
    if (normalizedUser.shop_id) {
      localStorage.setItem('shop_id', String(normalizedUser.shop_id));
    }
    if (normalizedUser.cold_storage_id) {
      localStorage.setItem('cold_storage_id', String(normalizedUser.cold_storage_id));
    }

    localStorage.setItem('user_data', JSON.stringify(normalizedUser));
    setUser(normalizedUser);

    return {
      ...response,
      data: {
        ...response.data,
        user: normalizedUser
      }
    };
  };

  const login = async ({ email, password, twoFactorCode }: LoginRequest) => {
    const response = await apiService.login(email, password, twoFactorCode);
    return appliquerConnexion(response);
  };

  /**
   * Pour l'ecran de connexion tactile : la requete (PIN, empreinte...) a
   * deja ete faite ailleurs — ici on ne fait qu'appliquer sa reponse,
   * exactement comme pour le mot de passe classique.
   */
  const applyLoginResponse = (response: any) => appliquerConnexion(response);

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Continue logout even if server call fails
    }
    localStorage.removeItem('user_data');
    localStorage.removeItem('ferme_id');
    localStorage.removeItem('shop_id');
    localStorage.removeItem('cold_storage_id');
    setUser(null);
    window.location.href = '/login';
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  const hasRole = (role: string): boolean => {
    return user?.roles?.some(r => typeof r === 'string' ? r === role : r.name === role) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        applyLoginResponse,
        logout,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
